import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, MessageSquare, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /ad-approval/:token — public review page for a shared ad.
 * Reviewer sees the ad + all 3 concepts + can approve / reject / comment.
 */
export default function AdApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    axios.get(`${API}/ad-builder/approvals/${token}`)
      .then((r) => setData(r.data.project))
      .catch((e) => setErr(e?.response?.data?.detail || "Approval link not found"));
  }, [token]);

  const decide = async (decision) => {
    setBusy(decision);
    try {
      const { data: r } = await axios.post(
        `${API}/ad-builder/approvals/${token}/decision`,
        { decision, reviewer_name: name || "Anonymous", comment });
      setData(d => ({ ...d, approval: r.approval }));
      setComment("");
      toast.success(decision === "comment" ? "Comment saved"
                       : decision === "approve" ? "Approved" : "Rejected");
    } catch { toast.error("Failed"); }
    finally { setBusy(""); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white p-6 flex items-center justify-center">
      <div className="rounded-2xl bg-rose-500/10 ring-1 ring-rose-500/30 p-6 max-w-md text-center">
        <XCircle className="w-8 h-8 mx-auto text-rose-300 mb-2" />
        <div className="text-lg font-bold">{err}</div>
        <div className="text-white/60 text-sm mt-2">The link may have expired.</div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-950 text-white p-6 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );

  const backend = process.env.REACT_APP_BACKEND_URL;
  const manual = data.manual || {};
  const brand = data.brand || {};
  const biz = manual.business_name || brand.business_name?.value || "Ad review";
  const concepts = data.concepts || {};
  const renders = data.renders || {};
  const status = data.approval?.status || "pending";
  const decided = status !== "pending";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 text-white pb-20"
         data-testid="ad-approval">
      <div className="max-w-4xl mx-auto px-6 pt-14">
        <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">
          WebsiteTech · Ad review
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold">Please review this ad</h1>
        <div className="mt-2 text-white/60">Someone shared this ad with you before publishing. Your feedback helps them make the final call.</div>

        {decided && (
          <div className={`mt-6 rounded-xl px-4 py-3 ring-1 text-sm font-bold inline-flex items-center gap-2 ${
            status === "approved" ? "bg-emerald-500/20 ring-emerald-400/30 text-emerald-200"
              : "bg-rose-500/20 ring-rose-400/30 text-rose-200"}`}
            data-testid="ad-approval-decision-chip">
            {status === "approved" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {status} by {data.approval?.decided_by || "reviewer"}
          </div>
        )}

        {/* Business + primary render */}
        <div className="mt-8 rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6">
          <h2 className="text-2xl font-bold">{biz}</h2>
          {(renders["1x1"] || renders.landscape) && (
            <img src={`${backend}${(renders["1x1"] || renders.landscape).url}`}
                  alt="Primary render"
                  className="mt-4 rounded-xl max-h-96 mx-auto shadow-2xl"
                  data-testid="ad-approval-primary" />
          )}
        </div>

        {/* All 3 concepts */}
        <div className="mt-6 grid md:grid-cols-3 gap-3" data-testid="ad-approval-concepts">
          {["professional", "visual", "promotional"].map(k => {
            const c = concepts[k];
            if (!c) return null;
            return (
              <div key={k} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4"
                    data-testid={`ad-approval-concept-${k}`}>
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">{k}</div>
                <div className="mt-2 font-bold">{c.headline}</div>
                <div className="mt-1 text-sm text-white/70">{c.short_description}</div>
                {c.offer_line && (
                  <div className="mt-2 text-xs bg-amber-500/20 text-amber-200 rounded-full px-2 py-0.5 inline-block">
                    {c.offer_line}
                  </div>
                )}
                <div className="mt-2 text-xs font-bold uppercase tracking-widest">→ {c.cta}</div>
              </div>
            );
          })}
        </div>

        {/* Decision panel */}
        <div className="mt-8 rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
             data-testid="ad-approval-decision-panel">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-3">
            Your feedback
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="ad-approval-name"
                  className="w-full rounded-lg bg-white/10 ring-1 ring-white/10 px-3 py-2 text-sm mb-3" />
          <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                     placeholder="Comment (optional)…"
                     rows={3}
                     data-testid="ad-approval-comment"
                     className="w-full rounded-lg bg-white/10 ring-1 ring-white/10 px-3 py-2 text-sm mb-3" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => decide("approve")} disabled={!!busy || decided}
                     data-testid="ad-approval-approve"
                     className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
              {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve
            </button>
            <button onClick={() => decide("reject")} disabled={!!busy || decided}
                     data-testid="ad-approval-reject"
                     className="rounded-lg bg-rose-500 hover:bg-rose-400 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
              {busy === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Reject
            </button>
            <button onClick={() => decide("comment")} disabled={!!busy || !comment.trim()}
                     data-testid="ad-approval-comment-btn"
                     className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
              {busy === "comment" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              Just a comment
            </button>
          </div>
        </div>

        {/* Prior comments */}
        {(data.approval?.comments || []).length > 0 && (
          <div className="mt-6 rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
               data-testid="ad-approval-history">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-3">
              Previous feedback
            </div>
            <ul className="space-y-3">
              {data.approval.comments.map((c, i) => (
                <li key={i} className="text-sm border-l-2 border-white/10 pl-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{c.reviewer}</span>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">
                      {c.decision}
                    </span>
                    <span className="text-xs text-white/40">
                      {c.at ? new Date(c.at).toLocaleString() : ""}
                    </span>
                  </div>
                  {c.comment && <div className="text-white/70 mt-1">{c.comment}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
